import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { useToast } from '@/hooks/use-toast';
import { ChevronRight, ChevronLeft, Loader2, User, RefreshCw, ExternalLink, Sparkles } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';

interface WizardAgentAdsProps {
  onNext: () => void;
  onPrevious: () => void;
  siteId?: string;
}

interface RealtorAd {
  id: string;
  ad_type: string;
  image_url: string;
  is_active: boolean;
}

export function WizardAgentAds({ onNext, onPrevious, siteId }: WizardAgentAdsProps) {
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [ads, setAds] = useState<RealtorAd[]>([]);
  const [realtorProfileId, setRealtorProfileId] = useState<string | null>(null);

  useEffect(() => {
    if (siteId) {
      fetchAds();
    }
  }, [siteId]);

  const fetchAds = async () => {
    if (!siteId) return;

    try {
      // Get realtor profile
      const { data: profile } = await supabase
        .from('realtor_profiles')
        .select('id')
        .eq('site_id', siteId)
        .maybeSingle();

      if (profile) {
        setRealtorProfileId(profile.id);

        const { data: adsData } = await supabase
          .from('realtor_ads')
          .select('id, ad_type, image_url, is_active')
          .eq('realtor_profile_id', profile.id)
          .order('created_at', { ascending: false });

        setAds(adsData || []);
      }
    } catch (error) {
      console.error('Error fetching agent ads:', error);
    } finally {
      setLoading(false);
    }
  };

  const generateAds = async () => {
    if (!siteId || !realtorProfileId) return;

    setGenerating(true);
    try {
      const session = await supabase.auth.getSession();
      if (!session.data.session) throw new Error('Not authenticated');

      const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/generate-realtor-ad`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.data.session.access_token}`,
        },
        body: JSON.stringify({ siteId }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to generate ads');
      }

      toast({ title: 'Agent ads generated successfully!' });
      fetchAds();
    } catch (error: any) {
      toast({
        title: 'Error generating ads',
        description: error.message,
        variant: 'destructive',
      });
    } finally {
      setGenerating(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const buyerAds = ads.filter(a => a.ad_type === 'buyer');
  const sellerAds = ads.filter(a => a.ad_type === 'seller');
  const hasAds = ads.length > 0;

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <User className="h-5 w-5 text-primary" />
              <CardTitle>Agent Ads</CardTitle>
            </div>
            {hasAds && <Badge variant="default">{ads.length} ads</Badge>}
          </div>
          <CardDescription>
            Create promotional ads featuring your profile for buyer and seller pages.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {!realtorProfileId ? (
            <div className="text-center py-4 text-muted-foreground">
              Please complete your profile in the About Us step first.
            </div>
          ) : hasAds ? (
            <>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <h4 className="text-sm font-medium">Buyer Ads ({buyerAds.length})</h4>
                  <div className="grid gap-2">
                    {buyerAds.slice(0, 2).map((ad) => (
                      <div key={ad.id} className="aspect-[4/3] rounded-lg overflow-hidden bg-muted">
                        <img 
                          src={ad.image_url} 
                          alt="Buyer ad" 
                          className="w-full h-full object-cover"
                        />
                      </div>
                    ))}
                  </div>
                </div>
                <div className="space-y-2">
                  <h4 className="text-sm font-medium">Seller Ads ({sellerAds.length})</h4>
                  <div className="grid gap-2">
                    {sellerAds.slice(0, 2).map((ad) => (
                      <div key={ad.id} className="aspect-[4/3] rounded-lg overflow-hidden bg-muted">
                        <img 
                          src={ad.image_url} 
                          alt="Seller ad" 
                          className="w-full h-full object-cover"
                        />
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <div className="flex gap-2 justify-center">
                <Button variant="outline" onClick={() => window.open('/admin/ads', '_blank')}>
                  <ExternalLink className="h-4 w-4 mr-2" />
                  Manage All Ads
                </Button>
                <Button variant="outline" onClick={generateAds} disabled={generating}>
                  {generating ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <RefreshCw className="h-4 w-4 mr-2" />
                  )}
                  Regenerate
                </Button>
              </div>
            </>
          ) : (
            <div className="text-center space-y-4">
              <p className="text-sm text-muted-foreground">
                No agent ads have been created yet. Generate professional ads to promote your services.
              </p>
              <Button onClick={generateAds} disabled={generating}>
                {generating ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Sparkles className="h-4 w-4 mr-2" />
                )}
                Generate Agent Ads
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      <div className="flex items-center justify-between pt-4 border-t">
        <Button variant="outline" onClick={onPrevious}>
          <ChevronLeft className="h-4 w-4 mr-2" />
          Previous
        </Button>

        <Button onClick={onNext}>
          Continue
          <ChevronRight className="h-4 w-4 ml-2" />
        </Button>
      </div>
    </div>
  );
}
